import React, { useEffect, useState } from "react";
import { CompanyReportSectionConfig, Scope, SelectionTypeEnum } from "../models";
import { ReportSection } from "../ReportSection";
import { CompanyCustomersSubSection } from "./CompanyCustomersSubSection";
import { useQueryClient } from "react-query";
import pl from "shared/messages/pl";
import { ModesFilter, SegmentsFilter, YearsFilter } from "../filters";
import { getThemeMainColor } from "msTeamsUI/chartThemes/chartThemes";

export interface CustomersSectionProps {
    intermediaryId: number;
    scope: Scope | undefined;
    isMobileView: boolean;
    sectionConfig: CompanyReportSectionConfig;
}

export const CustomersSection: React.FC<CustomersSectionProps> = (props) => {
    const queryClient = useQueryClient();

    const [years, setYears] = useState(props.sectionConfig.periodDefinition.yearList);
    const [segments, setSegments] = useState(props.sectionConfig.company.segmentDefinition?.segmentList ?? []);
    const [modes, setModes] = useState(props.sectionConfig.modeList);

    useEffect(() => {
        const init = async () => {
            const queryKeys = [`customers_${props.sectionConfig.company.code}`];
            await Promise.all(queryKeys.map((x) => queryClient.invalidateQueries(x)));
        };
        init();
    }, [props.scope?.code]);

    const getFilters = () => {
        const result = [];

        result.push(
            <SegmentsFilter
                key={props.sectionConfig.company.code}
                segmentList={segments}
                onSelectedSegmentsChanged={(segmentList) => setSegments(segmentList)}
                isMobileView={props.isMobileView}
                atLeastOneAlwaysSelected
                multiple={props.sectionConfig.company.segmentDefinition?.selectionType === SelectionTypeEnum.MULTIPLE}
                prefix={props.sectionConfig.company.name}
            />
        );

        result.push(
            <YearsFilter
                key={"years-filter"}
                years={years}
                onSelectedYearsChanged={(years) => setYears(years)}
                isMobileView={props.isMobileView}
                multiple={props.sectionConfig.periodDefinition.selectionType === SelectionTypeEnum.MULTIPLE}
                atLeastOneAlwaysSelected={props.sectionConfig.periodDefinition.isRequired}
            />
        );

        result.push(
            <ModesFilter
                key={"modes-filter"}
                modes={modes}
                onSelectedModesChanged={(modes) => setModes(modes)}
                isMobileView={props.isMobileView}
            />
        );
        return result;
    };

    const content = (
        <CompanyCustomersSubSection
            key={props.sectionConfig.company.code}
            intermediaryId={props.intermediaryId}
            scope={props.scope}
            company={props.sectionConfig.company}
            selectedSegments={segments.filter((segment) => segment.selected).map((segment) => segment.code)}
            selectedYears={years.filter((year) => year.selected).map((year) => year.code)}
            selectedMode={modes.find((mode) => mode.selected)?.code ?? ""}
        />
    );

    return (
        <ReportSection
            isMobileView={props.isMobileView}
            title={`${pl.view360Card.reports.customers} ${props.sectionConfig.company.name}`}
            filters={getFilters()}
            reportContent={content}
            isCollapsible={props.isMobileView}
            highlightColor={getThemeMainColor(props.sectionConfig.company.themeName)}
        />
    );
};
